import { logException } from '@paradigm/logging/src/logging';
import { SuccessResp } from '@paradigm/api/src';

import { subscribeToChannels } from '#/websocket/useWsInstance';
import GrfqBboStore, { GrfqBboWithId } from '#/unified-rfqs/store/grfq-bbo';
import * as ApiRepository from '#/unified-rfqs/repositories/grfq-api';
import {
  createSingleGrfqWebSocket,
  GrfqNotification,
  SingleGrfqWebSocket,
} from '#/unified-rfqs/repositories/single-grfq-ws';
import { isFeatureFlagEnabled } from '../../feature-flags/utils';
import { CountMap } from './shared';

interface GrfqBboServiceDeps {
  api: typeof ApiRepository;
  grfqBboStore: GrfqBboStore;
}

type BboWsType = SingleGrfqWebSocket | null;

export default class GrfqBboService {
  private numSubsPerRfq = new CountMap<number>();
  private ws: BboWsType = null;
  private abortController: AbortController | null = null;

  constructor(private deps: GrfqBboServiceDeps) {}

  /**
   * Start web socket connection if not currently running.
   *
   * ATTENTION! Remember to `close()` when finished.
   */
  startWebSocket() {
    if (this.ws != null) return;

    const ws = createSingleGrfqWebSocket();

    ws.addEventListener('open', this.handleOpen);
    ws.addEventListener('close', this.handleClose);
    ws.addNotificationHandler(this.handleNotification);

    this.ws = ws;
  }

  close() {
    if (this.ws) {
      this.ws.removeEventListener('open', this.handleOpen);
      this.ws.removeEventListener('close', this.handleClose);
      this.ws.removeNotificationHandler(this.handleNotification);
      this.ws.close();
      this.abortController?.abort();
      this.ws = null;
    }
    this.numSubsPerRfq.clear();
  }

  getRfqBboObservable(rfqId: number) {
    return this.deps.grfqBboStore.item(rfqId);
  }

  async getGrfqBbo(rfqId: number) {
    const { api, grfqBboStore } = this.deps;

    // Return current bbo if there is an open connection
    const rfqBbo = this.getStoredBbo(rfqId);
    if (rfqBbo != null && this.numSubsPerRfq.get(rfqId) >= 1) {
      return {
        data: rfqBbo,
        status: 1,
        message: '',
        error: '',
        ok: true,
      } as SuccessResp<GrfqBboWithId>;
    }

    const resp = await api.getRfqBbo({ rfqId });
    if (resp.ok) {
      const grfqBbo: GrfqBboWithId = {
        ...resp.data,
        id: rfqId,
      };
      grfqBboStore.upsertMany([grfqBbo]);
    }

    return resp;
  }

  getStoredBbo(rfqId: number) {
    return this.deps.grfqBboStore.selectById(rfqId);
  }

  openRfqTickerChannel(rfqId: number) {
    this.numSubsPerRfq.increment(rfqId);

    // Subscribe on first subscriber
    if (this.numSubsPerRfq.get(rfqId) === 1 && isWsOpen(this.ws)) {
      const topicName = `grfq-mark-price-${rfqId}`;
      subscribeToChannels(
        this.ws,
        [getChannelName(rfqId)],
        topicName,
        this.abortController?.signal,
      ).catch((error) => {
        logException(`Unable to subscribe to ticker.${rfqId} channel`, error);
      });
    }
  }

  closeRfqTickerChannel(rfqId: number) {
    this.numSubsPerRfq.decrement(rfqId);

    // Unsubscribe when no more subscribers
    if (this.numSubsPerRfq.get(rfqId) === 0 && isWsOpen(this.ws)) {
      this.ws
        .request({
          method: 'unsubscribe',
          params: { channel: getChannelName(rfqId) },
          signal: this.abortController?.signal,
        })
        .catch((error) => {
          logException(
            `Unexpected failure unsubscribing to mark price ${rfqId} channel`,
            error,
          );
        });
    }
  }

  private handleNotification = (notification: GrfqNotification) => {
    const { grfqBboStore } = this.deps;

    const { rfq_id } = notification.data;
    switch (notification.channel) {
      case 'ticker':
        grfqBboStore.updateOne(rfq_id, {
          id: rfq_id,
          leg_prices: notification.data.legs,
          ...notification.data,
        });
        break;
      default:
        break;
    }
  };

  private handleClose = () => {
    this.abortController?.abort();
  };

  private handleOpen = () => {
    if (this.ws == null) {
      // Have we forgot to cleanup the handler before closing?
      const msg = 'Unexpected call to `handleOpen` without a valid web socket';
      logException(msg, new Error(msg));
      return;
    }

    this.abortController?.abort();
    this.abortController = new AbortController();

    const rfqIds = this.subscribedRfqs();
    const channels = rfqIds.map(getChannelName);
    const topicName = `grfq-mark-price-open:${rfqIds.join(',')}`;
    subscribeToChannels(
      this.ws,
      channels,
      topicName,
      this.abortController.signal,
    ).catch((error) => {
      logException(
        `Unable to subscribe to tickers on open: ${rfqIds.join(', ')}`,
        error,
      );
    });
  };

  /**
   * @returns List of currently subscribed RFQ's ids
   */
  private subscribedRfqs() {
    return [...this.numSubsPerRfq.keys()];
  }
}

function isWsOpen<T extends { readyState: number }>(ws: T | null): ws is T {
  return ws?.readyState === WebSocket.OPEN;
}

function getChannelName(
  rfqId: number,
): `ticker.${number}` | `ticker.${number}.500ms` {
  if (isFeatureFlagEnabled('feature_um_constant_time_rfq_ticker')) {
    return `ticker.${rfqId}.500ms`;
  }

  return `ticker.${rfqId}`;
}

export function createGrfqBboService(): GrfqBboService {
  const api = ApiRepository;
  const grfqBboStore = new GrfqBboStore();

  return new GrfqBboService({
    api,
    grfqBboStore,
  });
}

export function getGrfqBboService(): GrfqBboService {
  return createGrfqBboService();
}
